import { useStreetViewHeading } from "@/hooks/use-street-view-heading";
import {
  buildPropertyStreetViewImageUrl,
  buildPropertyStreetViewPanoLink,
} from "@/lib/street-view-heading";
import type { ReactNode } from "react";

export type PropertyLocationFields = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
  apn?: string | null;
  latitude?: number | null;
  longitude?: number | null;
};

/** Matches the license property tile: address on the left, APN and jurisdiction on the right. */
export type PropertyLocationLayout =
  | "operator"
  | "registrant"
  | "compact"
  | "stacked";

function PropertyAddressLines({
  location,
  firstSeenLabel,
}: {
  location: PropertyLocationFields;
  firstSeenLabel?: string;
}) {
  const locality = [
    location.city,
    [location.state, location.zipcode].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <>
      <p className="text-sm font-bold">Address</p>
      <p className="text-sm">{location.address1 || "Unknown"}</p>
      {location.address2 ? (
        <p className="text-sm">{location.address2}</p>
      ) : null}
      {locality ? <p className="text-sm">{locality}</p> : null}
      {firstSeenLabel ? (
        <div className="mt-2">
          <p className="text-sm font-bold">First seen</p>
          <p className="text-sm">{firstSeenLabel}</p>
        </div>
      ) : null}
    </>
  );
}

function ApnField({
  apn,
  extra,
}: {
  apn?: string | null;
  extra?: ReactNode;
}) {
  return (
    <div>
      <p className="text-sm font-bold">APN</p>
      <p className="text-sm">{apn ?? "Unknown"}</p>
      {extra}
    </div>
  );
}

function JurisdictionField({ name }: { name?: string | null }) {
  return (
    <div>
      <p className="text-sm font-bold">Jurisdiction</p>
      <p className="text-sm">{name ?? "Unknown"}</p>
    </div>
  );
}

function PropertyStreetAndMapViews({
  location,
  compact,
}: {
  location: PropertyLocationFields;
  compact: boolean;
}) {
  const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
  const latitude = location.latitude ?? null;
  const longitude = location.longitude ?? null;
  const hasCoordinates = latitude !== null && longitude !== null;

  if (process.env.NODE_ENV === "development" && !googleMapsApiKey) {
    console.warn(
      "Google Maps API key is missing. Street View and Map View embeds will not work.",
    );
  }

  const addressParts = [location.address1];
  if (location.address2) {
    addressParts.push(location.address2);
  }
  addressParts.push(location.city, location.state, location.zipcode);
  const fullAddress = addressParts.filter(Boolean).join(", ");

  const streetViewAim = useStreetViewHeading(
    fullAddress || null,
    hasCoordinates ? latitude : null,
    hasCoordinates ? longitude : null,
    hasCoordinates && Boolean(googleMapsApiKey),
  );

  const streetViewImageUrl = streetViewAim.isLoading
    ? null
    : buildPropertyStreetViewImageUrl({
        apiKey: googleMapsApiKey,
        propertyLatitude: hasCoordinates ? latitude : null,
        propertyLongitude: hasCoordinates ? longitude : null,
        cameraLatitude: streetViewAim.cameraLatitude,
        cameraLongitude: streetViewAim.cameraLongitude,
        panoId: streetViewAim.panoId,
        address: fullAddress,
      });

  const streetViewFullUrl = buildPropertyStreetViewPanoLink({
    propertyLatitude: hasCoordinates ? latitude : null,
    propertyLongitude: hasCoordinates ? longitude : null,
    cameraLatitude: streetViewAim.cameraLatitude,
    cameraLongitude: streetViewAim.cameraLongitude,
    address: fullAddress,
  });

  const mapViewLocation = fullAddress
    ? encodeURIComponent(fullAddress)
    : hasCoordinates
      ? `${latitude},${longitude}`
      : null;

  const mapViewEmbedUrl =
    mapViewLocation && googleMapsApiKey
      ? `https://www.google.com/maps/embed/v1/place?key=${googleMapsApiKey}&q=${mapViewLocation}&zoom=15`
      : null;

  const handleStreetViewClick = () => {
    if (streetViewFullUrl) {
      window.open(streetViewFullUrl, "_blank", "noopener,noreferrer");
    }
  };

  if (!hasCoordinates || !googleMapsApiKey) {
    return null;
  }

  return (
    <div className={compact ? "mt-2 md:mt-4" : "mt-4"}>
      <div
        className={
          compact
            ? "grid grid-cols-2 gap-2 md:gap-4"
            : "grid grid-cols-1 md:grid-cols-2 gap-4"
        }
      >
        <div>
          <div
            className={
              compact
                ? "relative w-full h-28 rounded-lg overflow-hidden border md:h-48"
                : "relative w-full h-48 rounded-lg overflow-hidden border"
            }
          >
            {streetViewImageUrl ? (
              <img
                src={streetViewImageUrl}
                alt="Street View of property"
                className="w-full h-full object-cover cursor-pointer"
                onClick={handleStreetViewClick}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleStreetViewClick();
                  }
                }}
                aria-label="Click to open Street View in a new tab"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xs">
                {streetViewAim.isLoading
                  ? "Loading Street View…"
                  : "Street View unavailable"}
              </div>
            )}
          </div>
        </div>
        <div>
          <div
            className={
              compact
                ? "relative w-full h-28 rounded-lg overflow-hidden border md:h-48"
                : "relative w-full h-48 rounded-lg overflow-hidden border"
            }
          >
            {mapViewEmbedUrl ? (
              <iframe
                src={mapViewEmbedUrl}
                className="w-full h-full"
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Map view of property"
                onError={(event) => {
                  console.error("Map View iframe error:", event);
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-muted text-muted-foreground text-xs">
                Map View unavailable
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function PropertyLocationSummary({
  location,
  jurisdictionName,
  hideJurisdiction = false,
  layout,
  firstSeenLabel,
  apnExtra,
  aside,
  below,
}: {
  location: PropertyLocationFields;
  jurisdictionName?: string | null;
  hideJurisdiction?: boolean;
  layout: PropertyLocationLayout;
  firstSeenLabel?: string;
  /** Extra content under the APN value, such as the APN the requester entered. */
  apnExtra?: ReactNode;
  /** Extra fields under the address when the operator layout is two columns. */
  aside?: ReactNode;
  /** Full-width content between the identity row and the maps. */
  below?: ReactNode;
}) {
  const address = (
    <PropertyAddressLines location={location} firstSeenLabel={firstSeenLabel} />
  );
  const apn = <ApnField apn={location.apn} extra={apnExtra} />;
  const jurisdiction = hideJurisdiction ? null : (
    <JurisdictionField name={jurisdictionName} />
  );

  let identity: ReactNode;
  if (layout === "compact") {
    identity = (
      <div className="flex flex-wrap items-start gap-x-6 gap-y-2">
        <div className="min-w-0">{address}</div>
        {apn}
        {jurisdiction}
        {aside}
      </div>
    );
  } else if (layout === "operator") {
    identity = (
      <div
        className="grid gap-4 grid-cols-1 md:grid-cols-2 items-start"
        data-property-location=""
      >
        <div>{address}</div>
        <div className="space-y-4 md:col-start-2 md:row-start-1">
          {apn}
          {jurisdiction}
        </div>
        {aside ? <div className="space-y-4 md:col-start-1">{aside}</div> : null}
      </div>
    );
  } else if (layout === "registrant") {
    identity = (
      <div className="grid gap-4 grid-cols-1 md:grid-cols-3 items-start">
        <div>{address}</div>
        {apn}
        {jurisdiction}
        {aside ? <div className="md:col-span-3">{aside}</div> : null}
      </div>
    );
  } else {
    identity = (
      <div className="space-y-2">
        <div>{address}</div>
        {apn}
        {jurisdiction}
        {aside}
      </div>
    );
  }

  return (
    <>
      {identity}
      {below}
      <PropertyStreetAndMapViews
        location={location}
        compact={layout === "compact"}
      />
    </>
  );
}
